import { Popover as BasePopover } from '@material-ui/core'
import formatDate from 'date-fns/format'
import { FC, memo, MouseEvent, useCallback, useMemo, useRef, useState } from 'react'
import BaseCalendar from 'react-calendar'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import Button from 'components/Button'
import { FlexDiv } from 'components/layout/flex'
import { StyledCaretDownIcon } from 'components/Select'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'

import BaseModal from '../BaseModal'

import 'react-calendar/dist/Calendar.css'

export type DatePickerDate = {
	from?: string
	to?: string
}

type BaseDatePickerProps = {
	date?: DatePickerDate
	prevLabel1?: string
	label1?: string
	prevLabel2?: string
	label2?: string
	activeDate?: Date
	onChange: (date: Date[]) => void
	anchorPosition?: 'top' | 'bottom'
	defaultActiveInput?: 'from' | 'to'
	iconFrom?: React.ReactNode
	iconTo?: React.ReactNode
}

type DatePickerModalProps = BaseDatePickerProps & {}

type DatePickerPopoverProps = BaseDatePickerProps & {
	anchorEl: HTMLElement | null
}

type DatePickerInputProps = BaseDatePickerProps &
	(({ type: 'modal' } & DatePickerModalProps) | { type: 'popover'; underline?: boolean })

export const DatePickerInput: FC<DatePickerInputProps> = memo(
	({
		type,
		date,
		prevLabel1,
		prevLabel2,
		label1,
		label2,
		defaultActiveInput,
		anchorPosition = 'top',
		iconFrom = <StyledCaretDownIcon />,
		iconTo = <StyledCaretDownIcon />,
		...props
	}) => {
		const [openModal, setOpenModal] = useState(false)
		const inputRageRef = useRef<HTMLDivElement>(null)
		const fromInputRef = useRef<HTMLInputElement>(null)
		const toInputRef = useRef<HTMLInputElement>(null)

		const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(inputRageRef.current)
		const [activeInput, setActiveInput] = useState<'from' | 'to'>()

		const [updatedFromDate, setUpdatedFromDate] = useState<string | undefined>()
		const [updatedToDate, setUpdatedToDate] = useState<string | undefined>()

		const clearData = useCallback(() => {
			setActiveInput(undefined)
			setUpdatedFromDate(undefined)
			setUpdatedToDate(undefined)
		}, [])

		const { initialDate, fromDate, formattedFromDate, formattedToDate, toDate } = useMemo(() => {
			if (date && date?.from && date?.to) {
				const dates = [new Date(date.from), new Date(date.to)].sort(
					(a, b) => a.getTime() - b.getTime()
				)

				return {
					fromDate: dates[0],
					formattedFromDate: formatDate(dates[0], 'MM/dd/yyyy'),
					toDate: dates[0],
					formattedToDate: formatDate(dates[1], 'MM/dd/yyyy'),
					initialDate: dates as [Date, Date],
				}
			}

			clearData()
			return {}
		}, [date, clearData])

		const { indicatorWidth, indicatorOffset } = useMemo(() => {
			let indicatorWidth = 0
			let indicatorOffset = 0
			const sizeCorrection = 20 //offset padding

			if (!activeInput || !inputRageRef.current || !fromInputRef.current || !toInputRef.current)
				return { indicatorWidth, indicatorOffset }

			if (activeInput === 'from') {
				indicatorWidth = fromInputRef.current.offsetWidth - sizeCorrection
				indicatorOffset =
					fromInputRef.current.getBoundingClientRect().left -
					inputRageRef.current.getBoundingClientRect().left +
					sizeCorrection / 2
			}

			if (activeInput === 'to') {
				indicatorWidth = toInputRef.current.offsetWidth - sizeCorrection
				indicatorOffset =
					toInputRef.current.getBoundingClientRect().left -
					inputRageRef.current.getBoundingClientRect().left +
					sizeCorrection / 2
			}

			return { indicatorWidth, indicatorOffset }
		}, [fromInputRef, toInputRef, activeInput, inputRageRef])

		const handleOnChange = useCallback(
			(date: Date[]) => {
				props.onChange(date.sort((a, b) => a.getTime() - b.getTime()))
				if (type === 'popover') {
					setAnchorEl(null)
				} else {
					setOpenModal(false)
				}
				clearData()
			},
			[type, props, clearData]
		)

		const handleOnClose = useCallback(() => {
			if (type === 'popover') {
				setAnchorEl(null)
			} else {
				setOpenModal(false)
			}

			clearData()
		}, [type, clearData])

		const handleNextDate = useCallback(
			(selectedDate: Date) => {
				if (activeInput === 'to') {
					setUpdatedToDate(formatDate(selectedDate, 'MM/dd/yyyy'))
					setActiveInput('from')
				} else {
					setUpdatedFromDate(formatDate(selectedDate, 'MM/dd/yyyy'))
					setActiveInput('to')
				}
			},
			[activeInput, setActiveInput, setUpdatedFromDate, setUpdatedToDate]
		)

		const handleInputClick = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				const target = event.target as HTMLDivElement
				const name = target.getAttribute('name')

				if (defaultActiveInput && !activeInput) {
					setActiveInput(defaultActiveInput)
				} else {
					setActiveInput(name as 'from' | 'to')
				}
				if (type === 'modal') {
					setOpenModal(true)
				} else {
					setAnchorEl(inputRageRef.current)
				}
			},
			[type, defaultActiveInput, activeInput]
		)

		return (
			<>
				<InputContainer ref={inputRageRef} className="input-container">
					{!!prevLabel1 && <Label hasDate={!!fromDate}>{prevLabel1}</Label>}
					<DateContainer
						name="from"
						active={activeInput === 'from'}
						withBorder={type === 'modal'}
						onClick={handleInputClick}
					>
						{!!label1 && <Label hasDate={!!fromDate}>{label1}</Label>}
						<StyledInput
							ref={fromInputRef}
							type="text"
							placeholder="MM/DD/YYYY"
							value={updatedFromDate ?? formattedFromDate ?? ''}
						/>
						{iconFrom}
					</DateContainer>
					{!!prevLabel2 && <Label hasDate={!!toDate}>{prevLabel2}</Label>}
					<DateContainer
						name="to"
						active={activeInput === 'to'}
						withBorder={type === 'modal'}
						onClick={handleInputClick}
					>
						{!!label2 && <Label hasDate={!!toDate}>{label2}</Label>}
						<StyledInput
							ref={toInputRef}
							type="text"
							placeholder="MM/DD/YYYY"
							value={updatedToDate ?? formattedToDate ?? ''}
						/>
						{iconTo}
					</DateContainer>
					{'underline' in props && props.underline && !!activeInput && (
						<Indicator className="indicator" offset={indicatorOffset} width={indicatorWidth} />
					)}
				</InputContainer>
				{type === 'popover' && (
					<DatePickerPopover
						initialDate={initialDate}
						onChange={handleOnChange}
						onClose={handleOnClose}
						anchorEl={anchorEl}
						handleNextDate={handleNextDate}
						anchorPosition={anchorPosition}
					/>
				)}
				{type === 'modal' && openModal && (
					<DatePickerModal
						initialDate={initialDate}
						onChange={handleOnChange}
						onClose={handleOnClose}
						handleNextDate={handleNextDate}
					/>
				)}
			</>
		)
	}
)

const DatePickerModal: FC<
	DatePickerModalProps & {
		onClose: () => void
		initialDate: [Date, Date] | undefined
		handleNextDate: (selectedDate: Date) => void
	}
> = memo(({ initialDate, handleNextDate, onChange, onClose }) => {
	const { t } = useTranslation()
	const [newDate, setNewDate] = useState<Date[]>()

	// Force re render calendar to refresh values
	const [key, setKey] = useState<number>(0)
	const onReset = () => setKey((prev) => prev + 1)

	return (
		<Modal onDismiss={onClose} title="Dates">
			<Divider height={1} divider />
			<Calendar
				key={key}
				onChange={(v) => {
					setNewDate(v as Date[])
				}}
				defaultValue={initialDate}
				returnValue="range"
				prev2Label={null}
				next2Label={null}
				borders
				selectRange
				onClickDay={handleNextDate}
				mobile
			/>
			<ButtonsContainer>
				<BaseButton variant="secondary" onClick={onReset}>
					{t('futures.market.calendar.modal.reset')}
				</BaseButton>
				<BaseButton
					disabled={!newDate?.length}
					variant="solid-yellow"
					onClick={() => onChange(newDate as Date[])}
				>
					{t('futures.market.calendar.modal.confirm')}
				</BaseButton>
			</ButtonsContainer>
		</Modal>
	)
})

export const DatePickerPopover: FC<
	DatePickerPopoverProps & {
		onClose: () => void
		initialDate: [Date, Date] | undefined
		handleNextDate: (selectedDate: Date) => void
		anchorPosition: 'top' | 'bottom'
	}
> = ({ initialDate, handleNextDate, onChange, onClose, anchorEl, anchorPosition }) => {
	const open = Boolean(anchorEl)
	const id = open ? 'datePicker' : undefined

	return (
		<Popover
			id={id}
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: anchorPosition === 'bottom' ? -10 : 44,
				horizontal: 'center',
			}}
			transformOrigin={{
				vertical: anchorPosition,
				horizontal: 'center',
			}}
		>
			<Calendar
				onChange={(v) => {
					onChange(v as Date[])
					onClose()
				}}
				defaultValue={initialDate}
				returnValue="range"
				prev2Label={null}
				next2Label={null}
				borders
				selectRange
				onClickDay={handleNextDate}
			/>
		</Popover>
	)
}

const Divider = styled(Spacer)`
	width: 100%;
	margin-bottom: 10px;
`

const ButtonsContainer = styled(FlexDiv)`
	width: 100%;
	justify-content: space-between;
	gap: 10px;
	padding: 30px 0px;
`

const BaseButton = styled(Button)`
	font-size: 14px;
	width: 100%;
`

const InputContainer = styled(FlexDiv)`
	justify-content: space-between;
	align-items: center;
	position: relative;
	width: 100%;
	cursor: pointer;
	gap: 10px;
`

const Label = styled(Body)<{ hasDate?: boolean }>`
	line-height: normal;
	color: ${(props) => props.theme.colors.selectedTheme.text[props.hasDate ? 'value' : 'label']};
`

const Indicator = styled.div<{ width: number; offset: number }>`
	position: absolute;
	width: ${(props) => props.width}px;
	left: ${(props) => props.offset}px;
	bottom: -13px;
	height: 2px;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.indicator};
	transition: all 0.3s ease-in-out;
`

const StyledInput = styled.input<{ value?: string }>`
	max-width: 90px;
	text-align: center;
	background: transparent;
	cursor: pointer;
	border: none;
	caret-color: transparent;
	pointer-events: none;
	&:focus {
		outline: none;
	}
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
`

const DateContainer = styled(FlexDiv)<{
	withBorder?: boolean
	active: boolean
	name: 'to' | 'from'
}>`
	align-items: center;
	input {
		font-size: 12px;
	}
	${(props) =>
		props.withBorder &&
		css`
			background-color: ${props.active
				? props.theme.colors.selectedTheme.button.fillHover
				: props.theme.colors.selectedTheme.input.background};
			border: ${(props) => props.theme.colors.selectedTheme.border};
			height: 39px;
			border-radius: 8px;
			align-items: center;
			padding: 10px 13px;
		`}
`

const Modal = styled(BaseModal)`
	& .card {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	}
	& .card-body {
		align-items: center;
		padding: 20px 20px 0px 20px;
	}
`

const Popover = styled(BasePopover)`
	& .MuiPopover-paper {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.containers.primary.background};
		border: ${(props) => props.theme.colors.selectedTheme.border};
		padding: 10px;
		border-radius: 10px;
	}
`

const Calendar = styled(BaseCalendar)<{ borders?: boolean; mobile?: boolean }>`
	width: ${({ mobile }) => (mobile ? '330' : '240')}px;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.primary.background};

	border: none;

	& button {
		border-radius: 4px;
		width: 32px;
		height: 32px;
		padding: 0px;
		margin: 4px 0;
	}

	& .react-calendar__tile--now {
		background: transparent;
	}

	& .react-calendar__tile:disabled {
		background: transparent;
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.outsideMonth};
	}

	& .react-calendar__tile--hover {
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.calendar.dayHover} !important;

		&:disabled {
			background: transparent !important;
		}
	}

	& .react-calendar__navigation {
		margin-bottom: 0px;
	}

	& .react-calendar__navigation__arrow {
		background-color: transparent !important;
		color: ${(props) => props.theme.colors.selectedTheme.text.label};
		font-size: 16px;
		padding: 0px;
	}

	& .react-calendar__navigation__label {
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
		font-weight: 700;
		&:enabled:hover {
			background-color: transparent;
		}
		pointer-events: none;
	}

	& .react-calendar__month-view__weekdays {
		justify-content: center;
		column-gap: ${({ mobile }) => (mobile ? '12' : '0')}px;
		padding-bottom: 8px;
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
		& abbr {
			text-decoration: none;
			text-transform: capitalize;
			font-size: 9px;
		}
		& > div {
			max-width: 32px;
		}
	}
	& .react-calendar__month-view__days__day {
		font-size: 12px;
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.month};
	}

	& .react-calendar__month-view__days__day--neighboringMonth {
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.outsideMonth};
	}

	& .react-calendar__month-view__days {
		justify-content: center;
		column-gap: ${({ mobile }) => (mobile ? '12' : '0')}px;
	}

	& .react-calendar__month-view__days__day {
		max-width: 32px;
		margin-right: 9px;
		&:hover {
			background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.dayHover};
		}
		&:active {
			background-color: ${(props) =>
				props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.background};
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.color};
		}
	}

	& .react-calendar__tile--active,
	.react-calendar__tile--rangeEnd {
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.color};
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.background} !important;
	}

	& .react-calendar__tile--range {
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.calendar.dayHover} !important;
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
	}

	& .react-calendar__tile--rangeStart,
	.react-calendar__tile--rangeEnd {
		background: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.background} !important;
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.selectedDay.color};
	}
`
